<template>
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
    />
  </div>
  <page-viewer
    v-for="(page, index) in storyPages"
    v-else
    v-show="index === currentPageIndex"
    :key="index"
    :page-content="page.content"
    :page-id="index"
    :ratio="page.ratio"
    :class="index === currentPageIndex ? 'active' : ''"
    @user-interaction="onUserInteraction"
  />
  <error-toast
    v-if="showErrorToast"
    :text="errorMessage"
    :reason="errorReason"
    @close="showErrorToast = false"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getOtsPages } from '../../api/sumsumApi';
import { getTrueLayerUrl } from '../../api/trueLayer';
import useContentLoader from '../../composables/useContentLoader';
import config from '../../config';
import Segment from '../../Segment';
import { useOtsJob } from '../../services/otsConfigService';
import ErrorToast from '../Common/ErrorToast.vue';
import storyLib from '../StoryLib';

const { PageViewer } = storyLib;

const route = useRoute();
const { getOtsJobConfig, getOtsFlowConfig } = useOtsJob();
const storyPages = ref([]);
const currentPageIndex = ref(0);
const story = ref(null);
const showErrorToast = ref(false);
const errorMessage = ref('');
const errorReason = ref('');

const otsJobConfig = await getOtsJobConfig();
const otsFlowConfig = await getOtsFlowConfig();
const {
  showContentLoader, toggleLoader,
} = useContentLoader();

const onUserInteraction = async data => {
  if (data && data.eventName === 'linkClicked' && data.link) {
    Segment.trackUserGot('OTSSuccess_LinkClicked', { otsJobConfig, otsFlowConfig, link: data.link });
    window.open(data.link, '_blank');
  }

  if (data && data.eventName === 'nextPage') {
    nextPage();
  }

  if (data && data.eventName === 'previousPage') {
    previousPage();
  }

  if (data && data.eventName === 'connectAccountsClicked') {
    await handleConnectAccountsClick();
  }
};

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('OTSSuccess_ConnectAccountsClicked', { otsJobConfig, otsFlowConfig });
  window.location.href = await getTrueLayerUrl(
    { jobId: otsJobConfig.jobId, fid: otsFlowConfig.flowId, fromSuccess: true },
    config.get().otsEnv,
  );
};

const previousPage = async () => {
  if (currentPageIndex.value > 0) {
    currentPageIndex.value -= 1;
  }
};

const nextPage = async () => {
  if (currentPageIndex.value < storyPages.value.length - 1) {
    Segment.trackUserInteraction('OTSSuccess_NextPageClicked', {
      otsJobConfig,
      otsFlowConfig,
      nextPage: storyPages.value[currentPageIndex.value + 1]?.name,
    });

    // Move to next page
    currentPageIndex.value += 1;
  } else {
    // This is the last page
    Segment.trackUserInteraction('OTSSuccess_Done', {
      otsJobConfig,
      otsFlowConfig,
    });
  }
};
onMounted(async () => {
  const queryParam = route.query || null;
  toggleLoader(true);

  try {
    story.value = await getOtsPages(otsFlowConfig.resultsPageFlow.flowId);
    console.log('story.value', story.value);
    if (story.value) {
      const storyData = typeof story.value === 'string'
        ? JSON.parse(story.value)
        : story.value;

      storyPages.value = storyData.pages || [];
    }
  } catch (error) {
    errorMessage.value = 'Failed to load your results. We are looking into it. Try again later.';
    errorReason.value = 'story_data_load_failed';
    showErrorToast.value = true;
    console.error('Story data load failed:', error);
  }

  toggleLoader(false);
  Segment.trackUserGot('OTSSuccess_PageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig, otsFlowConfig });
});

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';
.page {
  background-color: #E1F1F2;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
